import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, Subscription, take, map } from 'rxjs';

import { unsubscribe } from '@fcom/core/index';
import { FinnairBoundItem, FinnairOrder, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { BoundType } from '@fcom/dapi/index';
import { GaContext, LoaderType } from '@fcom/common/interfaces';
import { OrderStatus } from '@fcom/common-booking/index';
import { ButtonPosition } from '@fcom/ui-components/components/buttons';
import { CmsArticle } from '@fcom/core-api/interfaces';
import { FragmentService } from '@fcom/common/services';

import { CheckInService } from '../../../services/check-in.service';
import { CheckInStep } from '../../../enums';

@Component({
  selector: 'fin-check-child-safety-notice-route',
  templateUrl: 'check-in-child-safety-notice-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInChildSafetyNoticeRouteComponent implements OnInit, OnDestroy {
  readonly OrderStatus = OrderStatus;
  readonly LoaderType = LoaderType;
  readonly GaContext = GaContext;
  readonly BoundType = BoundType;
  readonly ButtonPosition = ButtonPosition;

  @ViewChild('errorMessage', { static: false })
  errorMessage: ElementRef;

  order$: Observable<FinnairOrder>;
  bound$: Observable<FinnairBoundItem>;
  passengers$: Observable<FinnairPassengerItem[]>;
  selectedForCheckInPassengers$: Observable<FinnairPassengerItem[]>;
  isAPISRequired$: Observable<boolean>;
  showErrorMessage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  nextStep$: Observable<CheckInStep>;
  content$: Observable<CmsArticle[]>;

  checkoutUrl: string;
  checkboxChecked = false;

  private subscriptions = new Subscription();

  constructor(
    private checkInService: CheckInService,
    private fragmentService: FragmentService
  ) {}

  ngOnInit(): void {
    this.order$ = this.checkInService.getOrder();
    this.bound$ = this.checkInService.getBound();
    this.isAPISRequired$ = this.checkInService.isAPISRequired();
    this.passengers$ = this.order$.pipe(map(({ passengers }) => passengers));
    this.selectedForCheckInPassengers$ = this.checkInService.getPassengers();
    this.nextStep$ = this.isAPISRequired$.pipe(
      map((isAPISRequired) => (isAPISRequired ? CheckInStep.APIS : CheckInStep.SEATMAP))
    );
    this.content$ = this.fragmentService.getFragment<CmsArticle>('MMB.fragments.kidsInBusinessNotice.url').pipe(
      take(1),
      map((fragment) => fragment.items as CmsArticle[])
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  continueToNextStep(): void {
    this.subscriptions.add(
      combineLatest([this.checkInService.isExitRowSeatConfirmationRequired(), this.nextStep$])
        .pipe(take(1))
        .subscribe(([exitSeatConfirmationRequired, nextStep]) => {
          if (exitSeatConfirmationRequired && nextStep === CheckInStep.SEATMAP) {
            this.checkInService.setShowExitRowSeatsNotification(true);
          } else if (this.checkboxChecked) {
            this.checkInService.triggerNextCheckInStep();
          } else {
            this.showErrorMessage$.next(true);
            setTimeout(() => {
              this.errorMessage?.nativeElement.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            }, 0);
          }
        })
    );
  }

  onCheckboxChange(checked: boolean): void {
    this.checkboxChecked = checked;
    if (checked) {
      this.showErrorMessage$.next(false);
    }
  }
}
